import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/common/Spinner'

// Routes that guest users are allowed to access
const GUEST_ALLOWED_PATHS = [
  '/projects',
  '/ped',
  '/content-review',
  '/notifications',
]

function isGuestAllowed(pathname: string): boolean {
  // Allow /projects and /projects/:id
  if (pathname === '/projects' || pathname.startsWith('/projects/')) return true
  return GUEST_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Maximum role level allowed (1=Super Admin, 2=Admin, 3=Manager, 4=Operative). Lower = more access. */
  maxLevel?: number
  /** If true, redirect guest users to /projects when they try to access this route */
  blockGuests?: boolean
}

export function ProtectedRoute({ children, maxLevel, blockGuests }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const fetchProfile = useAuthStore((state) => state.fetchProfile)
  const tokens = useAuthStore((state) => state.tokens)
  const location = useLocation()

  // Brief loading state while we verify the session on mount
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const verify = async () => {
      if (tokens?.accessToken) {
        // Always verify token on mount — triggers refresh if expired
        // so child components never fire queries with a stale token
        await fetchProfile()
      }
      setChecking(false)
    }
    verify()
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" label="Verifica autenticazione..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    )
  }

  // Guest restriction: redirect to /projects if path is not allowed
  if (user?.isGuest && (blockGuests || !isGuestAllowed(location.pathname))) {
    return <Navigate to="/projects" replace />
  }

  // Role level check: lower number = higher authority (1=SuperAdmin, 4=Operative)
  if (maxLevel !== undefined && user) {
    const userLevel = user.role?.level ?? 5
    if (userLevel > maxLevel) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}

/**
 * Convenience wrapper that blocks guest users from accessing the wrapped route.
 * Guests are redirected to /projects.
 */
export function GuestRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) return null

  if (user?.isGuest) {
    return <Navigate to="/projects" replace />
  }

  return <>{children}</>
}
